// IDs de pixel que NÓS mesmos inicializamos nesta carga (via ensureBrowserPixel).
// Sem isto, o clique (1ª interação que destrava o gate marketingReady) poderia rodar
// ensureBrowserPixel ANTES do _app: o pixel já entraria em fbq.getState().pixels e o
// _app receberia false ("pixel de terceiro"), então o PageView de browser NUNCA sairia.
// Marcando os ids "nossos" aqui, qualquer chamador posterior continua sendo tratado como
// dono (retorna true) — PageView/ViewContent têm guards próprios contra repetição.
const SELF_INITIALIZED_PIXELS = new Set<string>();

// Extrai o Pixel ID (dígitos) de um "base code" colado do Facebook, guardado no campo
// legado `pixel_facebook_head` (coluna `PixelFacebook`). Tolerante a aspas simples/duplas
// e aos formatos `fbq('init','123')` e `init('123')`. Retorna '' quando não encontra.
export function extractPixelId(snippet?: string | null): string {
    if (!snippet || typeof snippet !== 'string') return '';
    const patterns: RegExp[] = [
        /fbq\(\s*['"]init['"]\s*,\s*['"](\d{6,})['"]/i, // fbq('init', '123456789')
        /init\(\s*['"](\d{6,})['"]/i, // init('123456789')
        /['"](\d{10,})['"]/, // fallback: primeiro id de 10+ dígitos entre aspas
    ];
    for (const re of patterns) {
        const m = snippet.match(re);
        if (m?.[1]) return m[1];
    }
    return '';
}

// Resolve o Pixel ID para o `ReactPixel.init()`, tolerando a sujeira de dados real do
// `tbl_sites`: alguns tenants têm o ID limpo em `fb_id_pixel`, outros colaram o snippet HTML
// inteiro (ou até o access token) nesse campo, e ~353 têm o pixel só no snippet legado
// (`pixel_facebook_head`) sem `fb_id_pixel`. Ordem: ID já limpo → ID dentro do próprio campo
// sujo → ID extraído do snippet. Retorna '' quando não há nenhum ID válido (aí o pixel de
// browser não inicializa — caso genuíno de config ausente/incorreta, não bug de código).
export function resolvePixelId(fbIdPixel?: string | null, snippet?: string | null): string {
    const id = (fbIdPixel ?? '').trim();
    if (/^\d{6,20}$/.test(id)) return id; // fb_id_pixel já é um ID numérico limpo (aceita até 20 díg)
    const fromId = extractPixelId(id); // alguém colou o snippet/HTML no campo de ID
    if (fromId) return fromId;
    return extractPixelId(snippet); // extrai do PixelFacebook (base code legado)
}

// Garante o Pixel de BROWSER (window.fbq) SEM depender do `react-facebook-pixel`, cujo
// import()/init() falha silenciosamente no bundle de produção — o fbevents.js nunca carrega
// e window.fbq nunca é definido, então Test Events/Pixel Helper da Meta não detectam nada
// (só o CAPI server-side chegava). Injeta o base code CANÔNICO da Meta (o mesmo que o
// react-facebook-pixel injetaria internamente, e o mesmo mecanismo dos tenants cujo pixel
// já funciona via GTM). Idempotente e CONSERVADOR:
//  - window.fbq ausente  → injeta o base code + init(id) e retorna TRUE (nós criamos/possuímos
//    o pixel → o chamador dispara o track com eventID p/ dedup c/ CAPI). autoConfig fica no
//    DEFAULT (true) — é ele que liga o rastreio automático de cliques em botões e faz a
//    Ferramenta de Configuração de Eventos do Gerenciador funcionar (com false, botões não trackeavam).
//  - window.fbq presente → NÃO toca (ex.: pixel dentro do GTM do tenant) e retorna FALSE
//    (zero mudança de comportamento p/ quem já funciona → protege os ~300 sites).
export function ensureBrowserPixel(pixelId?: string | null): boolean {
    if (typeof window === 'undefined') return false;
    const id = (pixelId ?? '').trim();
    if (!/^\d{6,20}$/.test(id)) return false;
    const w = window as any;

    // Já inicializamos ESTE id nesta carga → continuamos "donos" (retorna true mesmo que ele
    // agora apareça em getState). O 2º chamador dispara o evento dele normalmente.
    if (SELF_INITIALIZED_PIXELS.has(id)) return true;

    // DEDUP correto: só desiste se o fbevents REAL já carregou E ESTE pixel já está ativo.
    // `getState()` só existe depois do fbevents.js carregar — um stub (de GTM ainda pendente
    // ou de extensão de debug) NÃO tem getState, então não é confundido com pixel ativo.
    try {
        const st = typeof w.fbq === 'function' && typeof w.fbq.getState === 'function' ? w.fbq.getState() : null;
        if (st && Array.isArray(st.pixels) && st.pixels.some((p: any) => p && p.id === id)) return false;
    } catch {
        /* getState pode lançar num stub — ignora e segue pro init */
    }

    // ⚠️ NÃO usar `if (w.fbq) return false`. Extensões de debug (ex.: "Meta Pixel Helper Pro")
    // PRÉ-DEFINEM window.fbq no document_start (getter/setter que intercepta as chamadas fbq()
    // p/ detectar o pixel). Com o gate antigo, saíamos sem NUNCA chamar fbq('init') → a extensão
    // não via pixel algum (e o CAPI mascarava o problema no Network). Aqui garantimos o fbq base
    // e SEMPRE chamamos fbq('init', id) via window.fbq (passa pelo wrapper da extensão → detecta).

    // Cria o fbq base canônico só se ainda não existir (não sobrescreve GTM/extensão).
    if (typeof w.fbq !== 'function') {
        const n: any = (w.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!w._fbq) w._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
    }

    // Carrega o fbevents.js UMA vez (é o que faz o /tr sair de verdade; autoConfig no default
    // habilita rastreio de botões + Ferramenta de Configuração de Eventos). Idempotente.
    if (!document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]')) {
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        (document.head || document.documentElement).appendChild(t);
    }

    // Init do NOSSO pixel via window.fbq → passa pelo wrapper de qualquer extensão (ela registra)
    // e o fbevents dedup-a init do mesmo id. É esta chamada que faltava com o gate antigo.
    w.fbq('init', id);
    SELF_INITIALIZED_PIXELS.add(id);
    return true;
}

// Gera um eventID único p/ dedup browser×CAPI: a Meta casa o evento do Pixel com o do
// Conversions API pelo mesmo eventID (mesmo eventName). Exportado porque o disparo CAPI
// server-side reusa este id. crypto.randomUUID quando disponível; senão, fallback com
// timestamp + aleatório em hex (colisão desprezível para dedup dentro da janela da Meta).
export function genMetaEventId(): string {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch {
        /* randomUUID pode não existir em ctx antigo/inseguro — cai no fallback */
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Lê um cookie do browser pelo nome. SSR-safe (sem `document` → undefined) e BLINDADO
// (nunca lança). Decodifica o valor (decodeURIComponent) e tolera '=' dentro do valor.
export function getCookie(name: string): string | undefined {
    try {
        if (typeof document === 'undefined' || !document.cookie) return undefined;
        for (const part of document.cookie.split('; ')) {
            const eq = part.indexOf('=');
            if (eq < 0) continue;
            if (part.slice(0, eq) === name) {
                return decodeURIComponent(part.slice(eq + 1));
            }
        }
    } catch {
        /* cookie inacessível → undefined */
    }
    return undefined;
}

// `_fbp` cru do cookie (setado pelo fbevents.js). Sem cookie → undefined. NUNCA inventar:
// a Meta descarta _fbp forjado e isso baixa a qualidade do evento (match quality).
export function getFbp(): string | undefined {
    return getCookie('_fbp') || undefined;
}

// Extrai o `fbclid` de um `_fbc` no formato canônico `fb.1.<creationTime>.<fbclid>`.
// O fbclid é tudo depois do 3º ponto (junta o resto p/ tolerar '.' improváveis no id).
function extractFbclidFromFbc(fbc?: string | null): string | undefined {
    if (!fbc) return undefined;
    const parts = fbc.split('.');
    if (parts.length < 4) return undefined;
    return parts.slice(3).join('.') || undefined;
}

const FBC_LS_KEY = 'meta:fbc';

// `_fbc` (parâmetro de clique da Meta). Prioridade:
//  (a) cookie `_fbc` — SEMPRE vence (valor canônico do fbevents.js; nunca sobrescrevemos);
//  (b) valor persistido em localStorage `meta:fbc` (JSON `{v, at}`) — o fbclid some da URL
//      após a 1ª navegação/SPA, então guardamos p/ os eventos seguintes;
//  (c) `fbclid` REAL na URL atual → constrói `fb.1.<ts>.<fbclid>`, PERSISTE e retorna. Um
//      fbclid novo DIFERENTE do persistido vence (fbclid mais recente ganha — padrão Meta).
// NUNCA gera _fbc sem um fbclid real. BLINDADO (localStorage pode lançar em modo privado/quota).
export function getFbc(): string | undefined {
    const fromCookie = getCookie('_fbc');
    if (fromCookie) return fromCookie; // (a) cookie sempre vence

    if (typeof window === 'undefined') return undefined;

    // fbclid REAL da URL (só existe em clique vindo de anúncio Meta).
    let fbclid: string | null = null;
    try {
        fbclid = new URL(window.location.href).searchParams.get('fbclid');
    } catch {
        fbclid = null;
    }

    // (b) valor persistido de uma navegação anterior.
    let stored: { v?: string; at?: number } | null = null;
    try {
        const raw = window.localStorage.getItem(FBC_LS_KEY);
        if (raw) stored = JSON.parse(raw);
    } catch {
        stored = null;
    }

    // (c) fbclid novo (ausente no persistido OU diferente dele) → constrói + persiste.
    if (fbclid) {
        const storedFbclid = extractFbclidFromFbc(stored?.v);
        if (!stored?.v || storedFbclid !== fbclid) {
            const built = `fb.1.${Date.now()}.${fbclid}`;
            try {
                window.localStorage.setItem(FBC_LS_KEY, JSON.stringify({ v: built, at: Date.now() }));
            } catch {
                /* quota/modo privado → só não persiste, ainda retornamos o valor construído */
            }
            return built;
        }
    }

    // Sem fbclid novo → usa o persistido se houver. Sem nada → undefined (NUNCA inventar).
    return stored?.v || undefined;
}

// Dispara o evento CAPI pelo endpoint INTERNO /api/meta-capi (fire-and-forget). O access token
// do Pixel NUNCA sai do servidor — aqui só mandamos o payload + sinais do browser (_fbp/_fbc);
// a resolução de token/pixel acontece no server-side, por host. SSR-safe e BLINDADO (qualquer
// falha é engolida — CAPI é best-effort e não pode afetar a navegação). Poupa o request quando
// o tenant nem tem Pixel ID resolvível (o endpoint ainda re-decide pelo TOKEN server-side — este
// gate é só otimização). `userData` já vem HASHEADO do chamador (forms de Lead); undefined aqui.
export function sendMetaCapiEvent(input: {
    site: any;
    eventName: string;
    eventId: string;
    customData?: Record<string, any>;
    userData?: Record<string, any>;
}): void {
    if (typeof window === 'undefined') return;
    try {
        const { site, eventName, eventId, customData, userData } = input;
        if (!eventName || !eventId) return;
        // Sem Pixel ID resolvível → nem tenta (o endpoint devolveria 204 mesmo).
        if (!resolvePixelId(site?.fb_id_pixel, site?.pixel_facebook_head)) return;

        // Coleta os sinais do browser ANTES de qualquer navegação (SPA troca a URL e some c/ fbclid).
        const fbp = getFbp();
        const fbc = getFbc();
        const eventSourceUrl = window.location.href;

        void fetch('/api/meta-capi', {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event_name: eventName,
                event_id: eventId,
                event_source_url: eventSourceUrl,
                custom_data: customData,
                fbp,
                fbc,
                user_data: userData,
                // codsite do tenant: o SERVIDOR só honra em TEST HOST (homologação/teste,
                // onde o Host não resolve tenant); em domínio real é ignorado por completo.
                codsite: site?.id,
            }),
        }).catch(() => {
            /* best-effort: falha de rede não pode afetar a navegação */
        });
    } catch {
        /* CAPI é best-effort — nunca propaga erro */
    }
}

/**
 * #1291 — `value` + `currency` do evento, ou NADA.
 *
 * Antes cada disparo mandava `currency: 'BRL'` fixo e `value: Number(venda || locacao || 0)`.
 * Em imóvel "sob consulta" (sem preço nenhum) isso virava `value: 0` com moeda junto — e é
 * disso que nascem os alertas de qualidade do Gerenciador da Meta ("moeda ausente", "valor em
 * formato inválido"): zero não é valor de conversão.
 *
 * Regra (Daniel, 02/09): sem preço real não manda nem valor nem moeda. Vazio é melhor que
 * valor errado, porque valor errado estraga o ROAS do cliente. Espalhar com `...` no payload:
 *   `{ ...metaPrecoParams(imovel?.preco_venda, imovel?.preco_loca), lead_source: '...' }`
 *
 * Venda tem prioridade sobre locação — mesma ordem que o site usa pra exibir preço.
 */
export function metaPrecoParams(
    precoVenda?: number | string | null,
    precoLocacao?: number | string | null
): { value: number; currency: string } | Record<string, never> {
    const valor = Number(precoVenda) > 0 ? Number(precoVenda) : Number(precoLocacao);
    if (!Number.isFinite(valor) || valor <= 0) return {};
    return { value: valor, currency: 'BRL' };
}

// Guard anti-dup módulo-level: um mesmo clique costuma disparar 2x (bubbling pai↔filho e
// duplo-clique). Segura a última chave por um curtíssimo intervalo. Fora do trackMetaEvent
// p/ persistir entre chamadas.
let lastMetaEvent: { key: string; at: number } | null = null;

// Dispara um evento de CLIQUE no Pixel de browser (window.fbq) com eventID p/ dedup com o
// CAPI. Best-effort e BLINDADO: falha de rastreamento JAMAIS pode quebrar a navegação, então
// todo o corpo roda dentro de try/catch vazio. No-op silencioso quando: SSR, tenant sem pixel,
// ou fbq indisponível. Convive com GTM: se o tenant tem tags Google, NÃO injeta o base code
// (não compete com o pixel de dentro do GTM) — só dispara se já houver window.fbq.
export function trackMetaEvent(
    site: any,
    eventName: string,
    params: Record<string, any> = {},
    opts?: { eventId?: string; custom?: boolean }
): void {
    if (typeof window === 'undefined') return;
    try {
        const pixelId = resolvePixelId(site?.fb_id_pixel, site?.pixel_facebook_head);
        if (!pixelId) return; // tenant sem pixel → no-op total

        // Limpa params: descarta undefined/null, NaN (números) e strings vazias/só-espaço.
        // Arrays e objetos passam intactos (ex.: content_ids[], custom data aninhado).
        const clean: Record<string, any> = {};
        for (const k of Object.keys(params)) {
            const v = params[k];
            if (v === undefined || v === null) continue;
            if (typeof v === 'number' && Number.isNaN(v)) continue;
            if (typeof v === 'string' && v.trim() === '') continue;
            clean[k] = v;
        }

        // Anti-dup: mesma (eventName + content_name) em <400ms = mesmo clique (bubbling/duplo).
        // Vale pras DUAS pernas (browser E CAPI) — um clique duplicado não pode gerar 2 pares
        // de eventos. Por isso o guard fica ANTES da divisão entre as pernas.
        const key = `${eventName}|${clean.content_name ?? ''}`;
        const now = Date.now();
        if (lastMetaEvent && lastMetaEvent.key === key && now - lastMetaEvent.at < 400) return;
        lastMetaEvent = { key, at: now };

        // eventId ÚNICO das duas pernas (a Meta dedupa Pixel×CAPI pelo mesmo id + eventName).
        // Calculado ANTES de decidir a perna browser, pois a perna CAPI precisa dele mesmo que
        // o fbq ainda não exista (GTM não carregado).
        const eventId = opts?.eventId || genMetaEventId();

        // ── PERNA BROWSER (window.fbq) ──
        // Mitigação GTM (anti double-PageView): mesmos campos do hasGoogleMarketingSnippetSource
        // do _app.tsx. Se o tenant tem tags Google, o pixel provavelmente vive dentro do GTM —
        // NÃO chamamos ensureBrowserPixel (não competir/duplicar); só disparamos se já houver fbq.
        // IMPORTANTE: isto bloqueia SOMENTE a perna browser — a perna CAPI abaixo NÃO depende dela.
        const hasGoogleMarketingSnippetSource = Boolean(
            site?.tag_google_head ||
            site?.tag_google_body ||
            site?.google_analytics_head ||
            site?.google_analytics_body
        );
        if (!hasGoogleMarketingSnippetSource) {
            ensureBrowserPixel(pixelId);
        }
        if (typeof (window as any).fbq === 'function') {
            // `opts.custom` = evento CUSTOMIZADO (nome fora do catálogo padrão da Meta) → tem que
            // sair por `trackCustom`; com `track` a Meta loga "evento não padrão" e o evento não
            // aparece direito no Gerenciador. Eventos padrão (Lead, ViewContent...) seguem em `track`.
            (window as any).fbq(opts?.custom ? 'trackCustom' : 'track', eventName, clean, { eventID: eventId });
        }
        // (sem fbq disponível a perna browser simplesmente não sai — NÃO damos return: a CAPI segue)

        // ── PERNA CAPI (server-side, via /api/meta-capi) — REGRA DE OURO ──
        // Só dispara CAPI AUTOMATICAMENTE quando ESTE helper gerou o eventId (opts.eventId ausente).
        // Se o chamador PASSOU opts.eventId, é porque ele gerencia o lado CAPI por conta própria
        // (ex.: forms de Lead com PII hasheada, Search com dedup via listagem) → aqui fica
        // browser-only. A CAPI NÃO depende da perna browser ter saído (fbq pode nem existir ainda).
        if (!opts?.eventId) {
            sendMetaCapiEvent({ site, eventName, eventId, customData: clean });
        }
    } catch {
        /* rastreamento é best-effort — nunca propaga erro pra navegação */
    }
}
